/* eslint-disable max-len */
/* eslint-disable max-lines-per-function */
import React, {useMemo, useRef, useState, useEffect, useCallback} from 'react';
import { TableUtility } from '@utility/dynamic-table';
import 'primereact/resources/themes/saga-blue/theme.css';
import 'primereact/resources/primereact.min.css';
import 'primeicons/primeicons.css';
import './projectUtilization.scss';
import { useAuth } from '../../../Hooks/useAuth';
import { getExportFileName, getExportFileNameWithDate } from '../../Delivery/utils';
import { appendServiceNameToURL, appendServicePortToURL } from '../../../Api/resources/serviceURLs';
import { serviceName } from '../../../Api/resources/serviceConstants';
import { PROJECT_UTILIZATION } from '../../../Api/resources/endPoint';
// export filename/sheet name will be taken from API response when available

const buildServiceURL = (service, path) => {
  const url = appendServiceNameToURL(service);
  if (process.env.NODE_ENV === 'production') {
    return process.env.REACT_APP_API_URL + url + path;
  }
  const port = appendServicePortToURL(service);
  const baseUrl = process.env.REACT_APP_API_URL.replace(/\/$/, '');
  return `${baseUrl}:${port}${url}${path}`;
};

const baseURL = buildServiceURL(serviceName.PROJECT_MS, '/api' + PROJECT_UTILIZATION);

const buildProjectDetailsUrl = (projectId) => (
  `/delivery/project-details?projectId=${encodeURIComponent(String(projectId))}`
);

const isProjectNameField = (field = '') => ['project_name', 'projectName'].includes(field);

const getProjectIdFromRow = (rowData = {}) => rowData?.project_id || rowData?.projectId || null;

const getProjectNameFromRow = (rowData = {}) => rowData?.project_name || rowData?.projectName || '';

// Helper functions removed - tableConfiguration now comes from backend

const toDateOrNull = (value) => {
  if (!value) return null;
  if (value instanceof Date) {
    return Number.isNaN(value.getTime()) ? null : value;
  }

  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const deriveDateRange = (item) => {
  if (Array.isArray(item?.range)) {
    return [item.range[0] ?? null, item.range[1] ?? null];
  }

  const startDate
    = toDateOrNull(item?.startDate)
      || toDateOrNull(item?.from)
      || toDateOrNull(item?.fromDate)
      || toDateOrNull(item?.start);

  const endDate
    = toDateOrNull(item?.endDate)
      || toDateOrNull(item?.to)
      || toDateOrNull(item?.toDate)
      || toDateOrNull(item?.end)
      || startDate;

  if (!startDate && !endDate) {
    return [null, null];
  }

  return [startDate, endDate];
};

const deriveStartOnlyRange = (item) => {
  const existingRange = Array.isArray(item?.range) ? item.range : [];
  const fromDate
    = toDateOrNull(existingRange[0])
      || toDateOrNull(item?.startDate)
      || toDateOrNull(item?.from)
      || toDateOrNull(item?.fromDate)
      || toDateOrNull(item?.start)
      || null;

  return [fromDate, null];
};

const deriveEndOnlyRange = (item) => {
  const existingRange = Array.isArray(item?.range) ? item.range : [];
  const endDate
    = toDateOrNull(existingRange[1])
      || toDateOrNull(item?.endDate)
      || toDateOrNull(item?.to)
      || toDateOrNull(item?.toDate)
      || toDateOrNull(item?.end)
      || null;

  return [null, endDate];
};

const isStartDateFilter = (item) => {
  const label = (item?.label || '').toString().toLowerCase();
  const field = (item?.field || '').toString().toLowerCase();
  return label.includes('start date') || field.includes('start_date') || field.includes('startdate');
};

const isEndDateFilter = (item) => {
  const label = (item?.label || '').toString().toLowerCase();
  const field = (item?.field || '').toString().toLowerCase();
  return label.includes('end date') || field.includes('end_date') || field.includes('enddate');
};

const isMonthFilter = (item) => {
  const template = (item?.template || '').toString().toLowerCase();
  const type = (item?.type || '').toString().toLowerCase();
  const pickerType = (item?.pickerType || '').toString().toLowerCase();
  const displayFormat = (item?.displayFormat || '').toString().toLowerCase();

  return (
    template === 'monthcalendar'
    || type === 'month'
    || pickerType === 'month'
    || displayFormat === 'yyyy-mm'
  );
};

const toMonthCalendarValue = (value) => {
  if (!value) {
    return null;
  }

  if (value instanceof Date) {
    if (Number.isNaN(value.getTime())) {
      return null;
    }
    return `${value.getMonth() + 1}/${value.getFullYear()}`;
  }

  const normalized = value.toString().trim();
  if (!normalized) {
    return null;
  }

  const yearMonthMatch = normalized.match(/^(\d{4})-(\d{1,2})$/);
  if (yearMonthMatch) {
    const [, year, month] = yearMonthMatch;
    return `${Number(month)}/${year}`;
  }

  const monthYearMatch = normalized.match(/^(\d{1,2})[/-](\d{4})$/);
  if (monthYearMatch) {
    const [, month, year] = monthYearMatch;
    return `${Number(month)}/${year}`;
  }

  return normalized;
};

const normalizeMenuItems = (menuItems = []) => (
  Array.isArray(menuItems)
    ? menuItems.map((menuItem) => ({
      ...menuItem,
      items: Array.isArray(menuItem?.items)
        ? menuItem.items.map((item) => {
          if (item?.type !== 'filter') {
            return item;
          }

          if (isStartDateFilter(item)) {
            return {
              ...item,
              template: 'dateRange',
              range: deriveStartOnlyRange(item),
              rangeMode: 'startOnly',
              rangePlaceholders: ['From', 'To'],
            };
          }

          if (isEndDateFilter(item)) {
            return {
              ...item,
              template: 'dateRange',
              range: deriveEndOnlyRange(item),
              rangeMode: 'endOnly',
              rangePlaceholders: ['From', 'To'],
            };
          }

          const normalizedTemplate = (item?.template || '').toString().toLowerCase();

          // Backend sends template: 'month' — map to 'monthCalendar' which is the
          // exact template string that routes to YearMonthCalendarFilter inside
          // @utility/dynamic-table (see processMenuItems.js line 303).
          if (normalizedTemplate === 'month' || isMonthFilter(item)) {
            return {
              ...item,
              template: 'monthCalendar',
              selectedValue: toMonthCalendarValue(item?.selectedValue ?? item?.value ?? null),
            };
          }

          // Dynamic-table supports dateRange template for date picker rendering.
          if (normalizedTemplate === 'date' || normalizedTemplate === 'daterange') {
            return {
              ...item,
              template: 'dateRange',
              range: deriveDateRange(item),
            };
          }

          return item;
        })
        : menuItem?.items,
    }))
    : []
);

// Normalize table configuration to ensure UI shows active filters and
// synchronize defaults with global filterConfiguration
const normalizeTableConfig = (data = {}) => {
  if (!data || typeof data !== 'object') return data;

  const tableConfig = Array.isArray(data.tableConfiguration) ? data.tableConfiguration : [];
  const filterConfig = Array.isArray(data.filterConfiguration) ? data.filterConfiguration : [];

  const filterConfigMap = filterConfig.reduce((acc, cfg) => {
    if (cfg && cfg.field) acc[cfg.field] = cfg;
    return acc;
  }, {});

  const normalizedTableConfig = tableConfig.map((col) => {
    const menuGroups = Array.isArray(col?.menuItems) ? col.menuItems : [];
    let hasActiveFilter = false;

    for (const group of menuGroups) {
      const items = Array.isArray(group?.items) ? group.items : [];
      for (const item of items) {
        if (!item || item?.type !== 'filter') continue;

        const fieldName = item.field || col.field;

        if (Array.isArray(item.selectedValues) && item.selectedValues.length > 0) {
          hasActiveFilter = true;
          if (filterConfigMap[fieldName]) {
            filterConfigMap[fieldName].defaultValue = item.selectedValues;
          }
        } else if (item.selectedValue !== undefined && item.selectedValue !== null && item.selectedValue !== '') {
          hasActiveFilter = true;
          if (filterConfigMap[fieldName]) {
            filterConfigMap[fieldName].defaultValue = item.selectedValue;
          }
        } else if (Array.isArray(item.range) && (item.range[0] != null || item.range[1] != null)) {
          hasActiveFilter = true;
          if (filterConfigMap[fieldName]) {
            filterConfigMap[fieldName].defaultValue = item.range;
          }
        } else if (item.value !== undefined && item.value !== null && item.value !== '') {
          hasActiveFilter = true;
          if (filterConfigMap[fieldName]) {
            filterConfigMap[fieldName].defaultValue = item.value;
          }
        }
      }
    }

    return {
      ...col,
      visible: col.visible === undefined ? true : col.visible,
      showFilterButtonInHeader: col.showFilterButtonInHeader ?? hasActiveFilter,
      filterVisible: col.filterVisible ?? hasActiveFilter,
    };
  });

  data.tableConfiguration = normalizedTableConfig;
  data.filterConfiguration = filterConfig;
  return data;
};

const ProjectUtilization = () => {
  const { userProfile } = useAuth();
  const tableContainerRef = useRef(null);
  const [tableScrollHeight, setTableScrollHeight] = useState('500px');
  const exportSnapshotRef = useRef({ tableData: [], months: [] });

  const handleProjectNameClick = useCallback(({ fieldPath, rowData }) => {
    if (!isProjectNameField(fieldPath)) {
      return;
    }

    const projectId = getProjectIdFromRow(rowData);
    if (!projectId) {
      return;
    }

    window.open(buildProjectDetailsUrl(projectId), '_blank', 'noopener,noreferrer');
  }, []);

  useEffect(() => {

    const handleResize = () => {
      setTableScrollHeight(
        `${Math.floor(window.innerHeight * 0.7)}px`,
      );
    };

    handleResize();
    window.addEventListener('resize', handleResize);

    return () => {
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const handleFilteredApiResponse = useCallback((response) => {
    if (!response?.data || typeof response.data !== 'object') return response;

    const apiResponse = response.data;
    const payload
      = apiResponse?.data && typeof apiResponse.data === 'object'
        ? apiResponse.data
        : apiResponse;

    const rawTableConfiguration = Array.isArray(payload.tableConfiguration)
      ? payload.tableConfiguration
      : [];

    const tableConfiguration = rawTableConfiguration.map((column) => {
      const menuItems = normalizeMenuItems(Array.isArray(column?.menuItems) ? column.menuItems : []);

      const hasFilterMenu = menuItems.some(
        (group) => Array.isArray(group?.items) && group.items.some((item) => item?.type === 'filter'),
      );

      // Check if any filter has active/default values
      const hasActiveFilters = menuItems.some(
        (group) => Array.isArray(group?.items) && group.items.some((item) => {
          if (item?.type !== 'filter') return false;
          // Check for selectedValue, value, or range with actual values
          const hasSelectedValue = item?.selectedValue !== undefined && item?.selectedValue !== null && item?.selectedValue !== '';
          const hasValue = item?.value !== undefined && item?.value !== null && item?.value !== '';
          const hasActiveRange = Array.isArray(item?.range) && (item.range[0] !== null || item.range[1] !== null);
          return hasSelectedValue || hasValue || hasActiveRange;
        }),
      );

      return {
        ...column,
        menuItems,
        filter: column?.filter ?? hasFilterMenu,
        filterVisible:
          column?.filterVisible ?? ((hasFilterMenu || hasActiveFilters) && column?.visible !== false),
        showFilterButtonInHeader:
          column?.showFilterButtonInHeader ?? ((hasFilterMenu || hasActiveFilters) && column?.visible !== false),
        isGlobalFilter: column?.isGlobalFilter ?? false,
      };
    });

    const filterConfiguration = Array.isArray(payload.filterConfiguration)
      ? payload.filterConfiguration
      : [];

    // attach computed arrays to payload for normalization
    payload.tableConfiguration = tableConfiguration;
    payload.filterConfiguration = filterConfiguration;

    // Normalize table configuration and sync defaults
    normalizeTableConfig(payload);

    const normalizedTableData = (Array.isArray(payload.tableData) ? payload.tableData : []).map((row) => {
      const projectId = getProjectIdFromRow(row);
      const projectName = getProjectNameFromRow(row);

      if (!projectId || !projectName) {
        return row;
      }

      const existingRowConfiguration = row?.rowConfiguration || {};
      const existingOnClick = Array.isArray(existingRowConfiguration?.onClick)
        ? existingRowConfiguration.onClick
        : [];

      const projectClickTargets = ['project_name', 'projectName']
        .filter((field) => !existingOnClick.some((item) => item?.field === field))
        .map((field) => ({ field }));

      return {
        ...row,
        rowConfiguration: {
          ...existingRowConfiguration,
          onClick: [...existingOnClick, ...projectClickTargets],
        },
      };
    });
    const normalizedMonths = Array.isArray(payload.months) ? payload.months : [];

    exportSnapshotRef.current = { tableData: normalizedTableData, months: normalizedMonths };

    response.data = {
      ...apiResponse,
      ...payload,
      tableConfiguration: Array.isArray(payload.tableConfiguration) ? payload.tableConfiguration : [],
      filterConfiguration: Array.isArray(payload.filterConfiguration) ? payload.filterConfiguration : [],
      tableData: normalizedTableData,
      months: normalizedMonths,
      GlobalFilterEnabled: payload.GlobalFilterEnabled ?? true,
    };
    return response;
  }, []);

  const configuration = useMemo(
    () => ({
      generalSettings: {
        enableApiFiltering: true,
        baseURL,
        userInfo: {
          emailId: userProfile?.email || '',
          permissions: userProfile?.permissions || {},
          roleDetails: userProfile?.roleDetails || [],
          employeeId: userProfile?.employeeId || '',
          globalColors: userProfile?.siteConfiguration?.global_colors,
        },
        apiEndpoints: {
          getFilteredData: {
            endpoint: '',
            enabled: true,
          },
          download: {
            endpoint: 'export',
            enabled: true,
            filename: (response) => response?.data?.fileName || getExportFileName('Proj-util-ProjectUtilization'),
            sheetName: (response) => response?.data?.sheetTabName || getExportFileNameWithDate('Proj-util-ProjectUtilization'),
          },
        },
        handler: {
          onCellClickHandler: handleProjectNameClick,
          handleFilteredApiResponse,
        },
        additionalPayloadData: {
          employeeId: userProfile?.employeeId || '',
          get months() {
            return exportSnapshotRef.current.months;
          },
        },
        headers: {
          token: userProfile?.access_token || '',
        },
        showToolBar: true,
        showPagination: false,
        enableFilters: true,
        toolBarSettings: {
          showFilterOption: true,
          showAddNew: false,
          showExpandCollapse: false,
          showDownloadExcel: true,
          downloadExcelSheetName: 'Project Utilization',
          showColumnCustomize: false,
          showHelp: false,
          showSearch: false,
          showSaveView: false,
          showToolBarHeading: true,
          toolBarHeading: 'Project Utilization',
          showReset: true,
          showFilters: true,
        },
      },
      tableSettings: {
        enableTreeTable: false,
        size: 'small',
        loaderSkeleton: {
          rows: 5,
          bodyCells: 1,
          columns: 8,
        },
        showExpandCollapse: false,
        tableScrollHeight,
        maxFrozenColumns: {
          desktop: 4,
          tablet: 2,
          mobile: 1,
        },
      },
    }),
    [userProfile, tableScrollHeight, handleFilteredApiResponse, handleProjectNameClick],
  );
  if (!userProfile?.access_token) {
    return null;
  }

  return (
    <div className="project-utilization-outer-layer">
      <div className='project-utilization-table-container' ref={tableContainerRef}>
        <div className='project-utilization-table-utility'>
          <TableUtility configuration={configuration} />
        </div>
      </div>
    </div>
  );
};
export default ProjectUtilization;
